import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, SlidersHorizontal, Download, Plus, Eye, Pencil, RefreshCw, Printer } from "lucide-react";
import { api, errMsg, downloadFile, API } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  ["all", "All", "सर्व"], ["eligible", "Eligible", "पात्र"], ["ineligible", "Ineligible", "अपात्र"],
  ["error", "Error / Deficiency", "त्रुटी"], ["pending", "Verification Pending", "पडताळणी प्रलंबित"],
  ["no_bank", "Without Bank A/c", "बँक नाही"],
];
const ELIG = ["Eligible", "Ineligible", "Error / Deficiency", "Correction Submitted", "Verification Pending", "Verified"];
const BANK = ["Available", "Not Available", "Incorrect Details", "Verification Pending", "Verified"];
const INST = ["Paid", "Pending", "Rejected", "Under Process"];
const ATT = ["Submitted", "Not Submitted", "Error", "Verification Pending"];
const LOC = ["Taluka Area", "Latur Municipal Corporation Area"];

const BULK = [
  ["mark_eligible", "Mark Eligible"], ["mark_ineligible", "Mark Ineligible"], ["mark_error", "Mark Error"],
  ["mark_error_resolved", "Mark Error Resolved"], ["bank_status", "Update Bank Status"],
  ["first_installment", "Update 1st Installment"], ["second_installment", "Update 2nd Installment"],
  ["attendance_submitted", "Mark Attendance Submitted"], ["attendance_not_submitted", "Mark Attendance Not Submitted"],
  ["add_remark", "Add Remark"],
];
const BULK_VALUES = { bank_status: BANK, first_installment: INST, second_installment: INST };

const Select = ({ label, value, onChange, options, testId }) => (
  <label className="block">
    <span className="mb-1 block text-[11px] font-medium text-slate-600">{label}</span>
    <select data-testid={testId} value={value || ""} onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm">
      <option value="">All</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </label>
);

export default function Beneficiaries() {
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "all";
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(() => Object.fromEntries([...params.entries()].filter(([k]) => k !== "tab")));
  const [showFilters, setShowFilters] = useState(false);
  const [options, setOptions] = useState({ talukas: [], institutions: [], courses: [], categories: [], academic_years: [] });
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selected, setSelected] = useState([]);
  const [selectAllFiltered, setSelectAllFiltered] = useState(false);
  const [bulk, setBulk] = useState({ action: "", value: "", reason: "" });
  const [confirmBulk, setConfirmBulk] = useState(false);

  const query = useMemo(() => ({ ...filters, tab: tab === "all" ? undefined : tab, search: search || undefined }), [filters, tab, search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/beneficiaries", { params: { ...query, page, page_size: pageSize } });
      setData(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, [query, page, pageSize]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/beneficiaries/options").then(({ data }) => setOptions(data)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(() => setPage(1), 0); return () => clearTimeout(t); }, [query]);

  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v || undefined }));
  const resetFilters = () => { setFilters({}); setSearch(""); setParams(tab === "all" ? {} : { tab }); };

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allVisibleSelected = data.items.length > 0 && data.items.every((i) => selected.includes(i.beneficiary_id));

  const runBulk = async () => {
    try {
      const { data: res } = await api.post("/beneficiaries/bulk", {
        ids: selected, action: bulk.action, value: bulk.value || null, reason: bulk.reason,
        filters: selectAllFiltered ? query : null, select_all_filtered: selectAllFiltered,
      });
      toast.success(`Bulk update completed for ${res.updated} beneficiaries`);
      setSelected([]); setSelectAllFiltered(false); setBulk({ action: "", value: "", reason: "" }); setConfirmBulk(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const exportAs = (fmt) => {
    const p = { ...query };
    if (selected.length && !selectAllFiltered) p.ids = selected.join(",");
    downloadFile(`/beneficiaries/export/${fmt}`, `beneficiaries.${fmt}`, p)
      .then(() => toast.success(`Export (${fmt.toUpperCase()}) completed`))
      .catch((e) => toast.error(errMsg(e)));
  };

  const from = data.total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, data.total);

  return (
    <div className="space-y-4" data-testid="beneficiaries-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-[#0f2545] sm:text-4xl">Beneficiary Management</h1>
          <p className="text-sm text-slate-500">Single master database · एकच मास्टर डेटाबेस</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {can("write") && (
            <Link to="/beneficiaries/new" data-testid="add-beneficiary-btn" className="flex items-center gap-1.5 rounded-md bg-[#0f2545] px-3 py-2 text-sm font-medium text-white hover:bg-[#16345f]">
              <Plus size={16} /> Add Beneficiary
            </Link>
          )}
          {can("export") && (
            <>
              <button data-testid="export-xlsx-btn" onClick={() => exportAs("xlsx")} className="flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm hover:bg-slate-50"><Download size={16} /> Excel</button>
              <button data-testid="export-csv-btn" onClick={() => exportAs("csv")} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm hover:bg-slate-50">CSV</button>
              <button data-testid="export-pdf-btn" onClick={() => exportAs("pdf")} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm hover:bg-slate-50">PDF</button>
              <button data-testid="print-btn" onClick={() => window.print()} className="flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm hover:bg-slate-50"><Printer size={16} /> Print</button>
            </>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-1">
        {TABS.map(([key, label, mr]) => (
          <button key={key} data-testid={`tab-${key}`}
            onClick={() => { const p = new URLSearchParams(params); key === "all" ? p.delete("tab") : p.set("tab", key); setParams(p); setPage(1); }}
            className={`rounded-t-md px-3 py-2 text-sm ${tab === key ? "border-b-2 border-[#0f2545] font-semibold text-[#0f2545]" : "text-slate-600 hover:text-[#0f2545]"}`}>
            {label} <span className="text-[10px] text-slate-400">{mr}</span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input data-testid="global-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search name, ID, application no., Aadhaar, mobile…"
          className="min-w-[240px] flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm" />
        <button data-testid="toggle-filters" onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm hover:bg-slate-50">
          <SlidersHorizontal size={16} /> Advanced Filters
        </button>
        <button data-testid="refresh-btn" onClick={load} className="rounded-md border border-slate-300 bg-white p-2 hover:bg-slate-50"><RefreshCw size={16} /></button>
      </div>

      {showFilters && (
        <div data-testid="filter-panel" className="grid gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-2 lg:grid-cols-4">
          <Select label="Eligibility Status" testId="filter-eligibility" value={filters.eligibility_status} onChange={(v) => setFilter("eligibility_status", v)} options={ELIG} />
          <Select label="Taluka" testId="filter-taluka" value={filters.taluka} onChange={(v) => setFilter("taluka", v)} options={options.talukas} />
          <Select label="Location" testId="filter-location" value={filters.location_type} onChange={(v) => setFilter("location_type", v)} options={LOC} />
          <Select label="Institution" testId="filter-institution" value={filters.institution} onChange={(v) => setFilter("institution", v)} options={options.institutions} />
          <Select label="Course" testId="filter-course" value={filters.course} onChange={(v) => setFilter("course", v)} options={options.courses} />
          <Select label="Category" testId="filter-category" value={filters.category} onChange={(v) => setFilter("category", v)} options={options.categories} />
          <Select label="Academic Year" testId="filter-year" value={filters.academic_year} onChange={(v) => setFilter("academic_year", v)} options={options.academic_years} />
          <Select label="Bank Status" testId="filter-bank" value={filters.bank_status} onChange={(v) => setFilter("bank_status", v)} options={BANK} />
          <Select label="First Installment" testId="filter-first" value={filters.first_installment_status} onChange={(v) => setFilter("first_installment_status", v)} options={INST} />
          <Select label="Second Installment" testId="filter-second" value={filters.second_installment_status} onChange={(v) => setFilter("second_installment_status", v)} options={INST} />
          <Select label="Attendance" testId="filter-attendance" value={filters.attendance_status} onChange={(v) => setFilter("attendance_status", v)} options={ATT} />
          <div className="flex items-end gap-2">
            <button data-testid="apply-filters" onClick={() => { setPage(1); load(); }} className="rounded-md bg-[#0f2545] px-3 py-2 text-sm text-white hover:bg-[#16345f]">Apply Filters</button>
            <button data-testid="reset-filters" onClick={resetFilters} className="rounded-md border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50">Reset</button>
          </div>
        </div>
      )}

      {(selected.length > 0 || selectAllFiltered) && can("bulk") && (
        <div data-testid="bulk-toolbar" className="flex flex-wrap items-end gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3">
          <div className="text-sm font-medium text-amber-900">
            {selectAllFiltered ? `${data.total} filtered` : `${selected.length}`} selected
          </div>
          <select data-testid="bulk-action" value={bulk.action} onChange={(e) => setBulk({ ...bulk, action: e.target.value, value: "" })}
            className="rounded-md border border-slate-300 px-2 py-1.5 text-sm">
            <option value="">Choose bulk action…</option>
            {BULK.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          {BULK_VALUES[bulk.action] && (
            <select data-testid="bulk-value" value={bulk.value} onChange={(e) => setBulk({ ...bulk, value: e.target.value })} className="rounded-md border border-slate-300 px-2 py-1.5 text-sm">
              <option value="">Select value…</option>
              {BULK_VALUES[bulk.action].map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          )}
          {bulk.action === "add_remark" && (
            <input data-testid="bulk-remark" value={bulk.value} onChange={(e) => setBulk({ ...bulk, value: e.target.value })} placeholder="Remark" className="rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
          )}
          <input data-testid="bulk-reason" value={bulk.reason} onChange={(e) => setBulk({ ...bulk, reason: e.target.value })} placeholder="Reason (for status changes)" className="min-w-[200px] rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
          <button data-testid="bulk-apply" disabled={!bulk.action} onClick={() => setConfirmBulk(true)} className="rounded-md bg-[#0f2545] px-3 py-2 text-sm text-white disabled:opacity-50">Apply</button>
          <button data-testid="bulk-export" onClick={() => exportAs("xlsx")} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">Export Selected</button>
          <button data-testid="bulk-clear" onClick={() => { setSelected([]); setSelectAllFiltered(false); }} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">Clear</button>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-[1200px] w-full text-sm">
            <thead className="sticky top-0 bg-slate-100 text-left text-xs uppercase tracking-wide text-slate-600">
              <tr>
                <th className="p-3"><input data-testid="select-all-visible" type="checkbox" checked={allVisibleSelected}
                  onChange={(e) => setSelected(e.target.checked ? [...new Set([...selected, ...data.items.map((i) => i.beneficiary_id)])] : selected.filter((s) => !data.items.some((i) => i.beneficiary_id === s)))} /></th>
                {["ID", "Name", "Mobile", "College", "Course", "Taluka", "Location", "Eligibility", "Bank", "1st Inst.", "2nd Inst.", "Attendance", "Updated", "Actions"].map((h) => <th key={h} className="whitespace-nowrap p-3">{h}</th>)}
              </tr>
            </thead>
            <tbody data-testid="beneficiary-table-body">
              {loading ? (
                <tr><td colSpan={15} className="p-10 text-center text-slate-500"><Loader2 className="mx-auto animate-spin" /></td></tr>
              ) : data.items.length === 0 ? (
                <tr><td colSpan={15} className="p-10 text-center text-slate-500">No beneficiaries match the current filters.</td></tr>
              ) : data.items.map((b) => (
                <tr key={b.beneficiary_id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="p-3"><input data-testid={`row-check-${b.beneficiary_id}`} type="checkbox" checked={selected.includes(b.beneficiary_id)} onChange={() => toggle(b.beneficiary_id)} /></td>
                  <td className="whitespace-nowrap p-3 font-mono text-xs">{b.beneficiary_id}</td>
                  <td className="whitespace-nowrap p-3 font-medium text-[#0f2545]">{b.full_name}</td>
                  <td className="whitespace-nowrap p-3">{b.mobile}</td>
                  <td className="p-3 text-xs">{b.institution}</td>
                  <td className="whitespace-nowrap p-3">{b.course}</td>
                  <td className="whitespace-nowrap p-3">{b.taluka}</td>
                  <td className="p-3 text-xs">{b.location_type}</td>
                  <td className="p-3"><StatusBadge value={b.eligibility_status} testId={`elig-${b.beneficiary_id}`} /></td>
                  <td className="p-3"><StatusBadge value={b.bank_status} /></td>
                  <td className="p-3"><StatusBadge value={b.first_installment_status} /></td>
                  <td className="p-3"><StatusBadge value={b.second_installment_status} /></td>
                  <td className="p-3"><StatusBadge value={b.attendance_status} /></td>
                  <td className="whitespace-nowrap p-3 text-xs text-slate-500">{b.updated_at ? new Date(b.updated_at).toLocaleDateString("en-IN") : "—"}</td>
                  <td className="whitespace-nowrap p-3">
                    <div className="flex gap-1">
                      <Link data-testid={`view-${b.beneficiary_id}`} to={`/beneficiaries/${b.beneficiary_id}`} className="rounded p-1.5 text-slate-600 hover:bg-slate-200" title="View"><Eye size={16} /></Link>
                      {can("write") && <Link data-testid={`edit-${b.beneficiary_id}`} to={`/beneficiaries/${b.beneficiary_id}/edit`} className="rounded p-1.5 text-slate-600 hover:bg-slate-200" title="Edit"><Pencil size={16} /></Link>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 p-3 text-sm">
          <div data-testid="pagination-info" className="text-slate-600">
            Showing {from}–{to} of {data.total.toLocaleString("en-IN")}
            {!selectAllFiltered && data.total > data.items.length && (
              <button data-testid="select-all-filtered" onClick={() => setSelectAllFiltered(true)} className="ml-3 text-[#0f2545] underline">Select all {data.total} filtered</button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <select data-testid="page-size" value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} className="rounded-md border border-slate-300 px-2 py-1.5">
              {[25, 50, 100, 250].map((n) => <option key={n} value={n}>{n} / page</option>)}
            </select>
            <button data-testid="prev-page" disabled={page === 1} onClick={() => setPage(page - 1)} className="rounded-md border border-slate-300 px-3 py-1.5 disabled:opacity-40">Prev</button>
            <span>Page {page} / {Math.max(1, Math.ceil(data.total / pageSize))}</span>
            <button data-testid="next-page" disabled={page >= Math.ceil(data.total / pageSize)} onClick={() => setPage(page + 1)} className="rounded-md border border-slate-300 px-3 py-1.5 disabled:opacity-40">Next</button>
          </div>
        </div>
      </div>

      {confirmBulk && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
          <div data-testid="bulk-confirm-modal" className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-[#0f2545]">Confirm Bulk Update</h3>
            <p className="mt-2 text-sm text-slate-600">
              You are about to update {selectAllFiltered ? data.total : selected.length} beneficiaries. Do you want to continue?
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button data-testid="bulk-cancel" onClick={() => setConfirmBulk(false)} className="rounded-md border border-slate-300 px-4 py-2 text-sm">Cancel</button>
              <button data-testid="bulk-confirm" onClick={runBulk} className="rounded-md bg-[#0f2545] px-4 py-2 text-sm text-white">Confirm</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
